import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 63. API VERSIONING
// Les APIs doivent être VERSIONNÉES : /api/v1/… puis plus tard /api/v2/…
// Ne jamais casser les intégrations existantes lors d'une évolution.
// Ce module gère les versions exposées, leur cycle de vie (active →
// deprecated → retired) et résout la version demandée par un appelant.
// ─────────────────────────────────────────────────────────────

export const API_VERSION_PREFIX = '/api';
export const CURRENT_API_VERSION = 'v1';
export const SUPPORTED_API_VERSIONS = ['v1', 'v2'];

export const VERSION_STATUSES: { key: string; label: string; className: string }[] = [
  { key: 'active', label: 'Active', className: 'bg-primary-100 text-primary-900' },
  { key: 'deprecated', label: 'Dépréciée', className: 'bg-accent-100 text-accent-900' },
  { key: 'retired', label: 'Retirée', className: 'bg-background-100 text-foreground-500' },
];

export function versionStatusMeta(key: string | null | undefined) {
  return VERSION_STATUSES.find((s) => s.key === key) || VERSION_STATUSES[0];
}

export interface CoreApiVersion {
  id: number;
  version: string;
  label: string | null;
  status: string | null;
  release_notes: string | null;
  is_default: boolean | null;
  deprecated_at: string | null;
  sunset_at: string | null;
  created_at: string | null;
}

export async function listApiVersions(): Promise<CoreApiVersion[]> {
  const { data, error } = await supabase
    .from('core_api_versions')
    .select('*')
    .order('version', { ascending: true });
  if (error) throw error;
  return (data || []) as CoreApiVersion[];
}

export async function getDefaultVersion(): Promise<string> {
  const { data } = await supabase
    .from('core_api_versions')
    .select('version')
    .eq('is_default', true)
    .maybeSingle();
  return (data?.version as string) || CURRENT_API_VERSION;
}

export async function createApiVersion(input: {
  version: string;
  label: string;
  releaseNotes?: string | null;
}): Promise<void> {
  const { error } = await supabase.from('core_api_versions').insert({
    version: input.version,
    label: input.label,
    status: 'active',
    is_default: false,
    release_notes: input.releaseNotes ?? null,
  });
  if (error) throw error;
}

export async function setApiVersionStatus(
  id: number,
  status: 'active' | 'deprecated' | 'retired',
  sunsetAt?: string | null,
): Promise<void> {
  const { error } = await supabase
    .from('core_api_versions')
    .update({
      status,
      deprecated_at: status === 'deprecated' ? new Date().toISOString() : null,
      sunset_at: sunsetAt ?? null,
    })
    .eq('id', id);
  if (error) throw error;
}

export async function setDefaultApiVersion(id: number): Promise<void> {
  await supabase.from('core_api_versions').update({ is_default: false }).eq('is_default', true);
  const { error } = await supabase.from('core_api_versions').update({ is_default: true }).eq('id', id);
  if (error) throw error;
}

export async function deleteApiVersion(id: number): Promise<void> {
  const { error } = await supabase.from('core_api_versions').delete().eq('id', id).eq('is_default', false);
  if (error) throw error;
}

/** Construit un chemin versionné : versionedPath('v1', 'customers') → /api/v1/customers */
export function versionedPath(version: string, path: string): string {
  const clean = (path || '').replace(/^\/+/, '');
  return `${API_VERSION_PREFIX}/${version}/${clean}`;
}

/** Extrait la version d'un chemin entrant, ou renvoie la version par défaut. */
export function parseVersionFromPath(pathname: string): { version: string; rest: string } {
  const m = (pathname || '').match(/^\/api\/(v\d+)\/?(.*)$/i);
  if (m) return { version: m[1].toLowerCase(), rest: m[2] || '' };
  return { version: CURRENT_API_VERSION, rest: (pathname || '').replace(/^\/+/, '') };
}

/** Politique de compatibilité affichée à l'utilisateur. */
export const VERSIONING_POLICY = [
  'Une version publiée ne casse jamais une intégration existante.',
  'Les évolutions incompatibles sont livrées sous une nouvelle version (/api/v2/…).',
  'Une version dépréciée reste disponible avec un préavis (Sunset) avant retrait.',
  'L’en-tête de réponse indique la version et le statut de dépréciation.',
];

export interface VersioningStats {
  total: number;
  active: number;
  deprecated: number;
  current: string;
}

export async function fetchVersioningStats(): Promise<VersioningStats> {
  const versions = await listApiVersions();
  return {
    total: versions.length,
    active: versions.filter((v) => v.status === 'active').length,
    deprecated: versions.filter((v) => v.status === 'deprecated').length,
    current: versions.find((v) => v.is_default)?.version || CURRENT_API_VERSION,
  };
}